import { LoadingButton } from '@mui/lab';
import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, FormControl,
    TextField, Typography,
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { SeasonShowContext } from '../../../Providers/SeasonShowProvider';
import { SnackbarContext } from '../../../Providers/SnackbarProvider';
import { useNavigate } from 'react-router-dom';
import { graphql } from 'gql';
import { namedOperations } from 'gql/graphql';

const CreateEpisodeForSeason = graphql(`
    mutation CreateEpisode_AddEpisodeDialog($input: [EpisodeCreateInput!]!) {
        createEpisodes(input: $input) {
            episodes {
                id
                title
            }
        }
    }`);

type Props = {
    open: boolean;
    onClose: () => void;
}

export default function AddEpisodeDialog({ open, onClose }: Props) {
    const navigate = useNavigate();

    const { selectedSeason } = useContext(SeasonShowContext);
    const { success, error } = useContext(SnackbarContext);

    const [title, setTitle] = useState<string>();

    useEffect(() => {
        if (open) {
            setTitle(undefined);
        }
    }, [open])

    const [createEpisodeForSeason, { loading }] = useMutation(CreateEpisodeForSeason);

    const handleSave = async () => {
        try {
            if (!title) {
                error('Title is required');
                return;
            }

            const result = await createEpisodeForSeason({
                variables: {
                    input: [{
                        title,
                        season: {
                            connect: {
                                where: {
                                    node: {
                                        id: selectedSeason!.id,
                                    },
                                },
                            },
                        },
                    }],
                },
                refetchQueries: [namedOperations.Query.GetEpisodesForSeason_Layout],
            });
            const episode = result.data?.createEpisodes.episodes[0];

            if (!episode) {
                error('Failed to create show');
                return;
            }

            navigate(`/episode/${episode.id}`);

            success('Episode created');
            handleClose();
        } catch (e) {
            // @ts-ignore
            error('Failed to create episode. ' + e.message);
        }
    }

    const handleClose = () => {
        setTitle(undefined);
        onClose();
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            handleSave();
        }
    }

    if (!selectedSeason) return null;

    return (
        <Dialog open={open}>
            <DialogTitle>Add Episode</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1, mb: 1 }}>
                    <Typography>Season: {selectedSeason!.title}</Typography>
                </Box>
                <FormControl component="fieldset">
                    <TextField
                        sx={{ mt: 1, mb: 1 }}
                        label="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant='text'>Cancel</Button>
                <LoadingButton
                    onClick={handleSave}
                    disabled={!title}
                    variant='contained'
                    loading={loading}
                >
                    Save
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}